/* eslint-disable no-unused-vars */
import React, { Dispatch, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import moment from "moment";
import QueryBuilderSharpIcon from '@mui/icons-material/QueryBuilderSharp'
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import { FileUploader } from "react-drag-drop-files";
import TaskAltSharpIcon from '@mui/icons-material/TaskAltSharp';
import { Avatar, Button, Chip, Dialog, ListItemIcon, Menu, MenuItem, Radio, RadioProps } from "@mui/material";
import { AnyAction } from "redux";
import Teachr from "src/Objects/models/Teachr";
import TeachrRepository from "src/Repositories/TeachrRepository";
import { userTeachrSetAccountAction } from "src/Redux/Actions/UserActions";
import Functions from "../Helpers/Functions";
import { status } from "../Enum/Enum";
import FormInput from "../Components/Ui/FormInput";
import { DataProposalBis, DataPunctual, dataModel, dataTryAccept, lisActionInterface, paramActionsInterface, statusInterface, updatePaginationInterface, updatePaginationInterfaceBis } from "../Objects/interfaces/AppInterface";
import PunctualCourseRepository from "../Repositories/PunctualCourseRepository";
import { APIErrorEnum } from "../Objects/enums/APIErrorEnum";
import { describeOverlapApiError } from "../App/DeclarationCourse/overlapMessages";
import CourseProposal from "../Objects/models/CourseProposal";
import CourseProposalRepository from "../Repositories/CourseProposalRepository";
import { RESET_END_DECLARE_COURSE, SET_COURSE_DECLARATION_INFO } from "../Redux/Reducers/DeclarationCourseReducers";
import { PunctualCourseDashboard } from "../Objects/models/PunctualCourseDashboard";
import { DELETE_COURSE } from "../Redux/Actions/PunctualCourseActions";
import { store } from "../Redux/store";
import { SHOW_ERROR, SHOW_WARNING } from "../Redux/Reducers/MessageReducers";
import TravelRepository from "../Repositories/TravelRepository";
import TransportSubscriptionRepository from "../Repositories/TransportSubscriptionRepository";
import TravelStore from "../App/DeclarationCourse/Travel/TravelStore";
import { TdpDescEnum, TdpEnum, TdpEnumColor } from "../Enum/TdpEnum";
import { SponsorshipDescEnum, SponsorshipEnum, SponsorshipEnumColor } from "../Enum/SponsorshipEnum";
import { DeclarationStatusEnum, DeclarationStatusEnumColor, UrssafServiceStatEnum, UrssafServiceStatEnumColor, UrssafServiceStatusDescription } from "../Enum/UrssafStatusEnum";
import ImageRepository from "../Repositories/ImageRepository";
import AddressRepository from "../Repositories/AddressRepository";
import { Address } from "../Objects/models/Address";
import { AddressFields } from "../Components/Ui/AddressDetailed";
import { getBrandConfig } from "../config/brandConfig";
import { ENV_DECLARONS, ENV_VERIFIED_TRAINER, getActiveAppBrandConfig, getActiveClientBrandConfig, getActivePrestationBrandConfig, isBrandActive } from "../config/environment.config";
import { REDUX_TYPE_MAP } from "../config/COURSE_TYPE_CONFIG";
import { iconMoney } from "./Images";

const fileTypes = ["PDF", "PNG", "JPEG", "JPG"];


export const createImageRef = async (file: File, label: string): Promise<string> => {
  const response = await ImageRepository.createImage(new File([file], label, { type: file.type }));
  return response['@id'] as string;
};

// Compte prof actif, même règle que le backend (Teachr::isAccountActive) : code de validation, AE, SAP signé,
// conditions de service déposées. Seul état qui autorise la déclaration d'un cours.
export const isTeachrAccountActive = (teachr?: Teachr | null): boolean => {
  if (!teachr) return false;
  if (typeof teachr.accountActive === 'boolean') return teachr.accountActive;
  return !!(teachr.isValidated && teachr.isAE && teachr.signSap);
};

export const prepareTeachrForUpdate = (teachr: Teachr, updates: Partial<Teachr>): Teachr => {
  const updatedTeachr = { ...teachr, ...updates };

  // Convertir les objets en références si nécessaire
  const proofFields = ['proofIdentity', 'proofIdentity2', 'proofIsAE', 'proofSignSap', 'proofMandat'];
  proofFields.forEach(field => {
    if (updatedTeachr[field as keyof Teachr] && typeof updatedTeachr[field as keyof Teachr] === 'object') {
      (updatedTeachr[field as keyof Teachr] as any) = (updatedTeachr[field as keyof Teachr] as any)['@id'];
    }
  });

  return updatedTeachr;
};

export const updateTeachrData = async (updatedTeachr: Teachr, dispatch: any, setLoading?: (value: React.SetStateAction<boolean>) => void, nextStep?: () => void,) => {
  try {
    const response = await TeachrRepository.updateTeachr(updatedTeachr);
    const serializedTeachr = JSON.parse(JSON.stringify(response));
    dispatch(userTeachrSetAccountAction(serializedTeachr));
    if (setLoading) setLoading(false);
    if (nextStep) nextStep()
  } catch (error) {
    console.warn('Erreur lors de la mise à jour:', error);
    if (setLoading) setLoading(false);
  }
}

/**
 * Crée une adresse pour un teachr à partir des champs d'adresse détaillée.
 * Retourne l'IRI de l'adresse créée, ou null en cas d'erreur.
 */
export const createTeachrAddress = async (
  addressFields: AddressFields,
  teachrIri: string,
  teachrId: number,
): Promise<string | null> => {
  const streetNumber = [addressFields.numeroVoie, addressFields.lettreVoie].filter(Boolean).join(' ');
  const streetName = [addressFields.typeVoie, addressFields.libelleVoie].filter(Boolean).join(' ');
  const formattedAddress = [
    streetNumber,
    streetName,
    addressFields.complement,
    addressFields.lieuDit,
    addressFields.codePostal,
    addressFields.ville,
  ].filter(Boolean).join(' ');

  const address = new Address(
    'googlePlaceId',
    streetNumber,
    streetName,
    addressFields.ville,
    'department',
    'region',
    'France',
    addressFields.codePostal,
    'latitude',
    'url',
    formattedAddress,
    teachrIri,
    true,
    '',
    '',
    '',
  );
  address.longitude = '0';
  address.sessionToken = '';

  try {
    const response = await AddressRepository.createAddressForTeacher(address, teachrId);
    return (response as any)['@id'] ?? null;
  } catch (e) {
    console.error('Erreur création adresse teachr:', e);
    return null;
  }
};

export const valideDateBeforeAddClient = (dateCourse: moment.Moment, startAt: Date) => {
  if (moment(dateCourse).format("YYYY-MM-DD") < moment(startAt).format("YYYY-MM-DD")) {
    return {
      error: moment(dateCourse).format("YYYY-MM-DD") < moment(startAt).format("YYYY-MM-DD"),
      message: `La date de début ${getActivePrestationBrandConfig("", "des ", "de la ")} ne peut pas être antérieure à la validation du compte d’avance immédiate de l’élève.`
    }
  }
  if (moment(dateCourse).format("YYYY-MM") < moment().format("YYYY-MM")) {
    return {
      error: moment(dateCourse).format("YYYY-MM") < moment().format("YYYY-MM"),
      message: `Vous ne pouvez pas déclarer les cours pour le mois qui est déjà passé. Veuillez modifier la date.`
    }
  }
  return {
    error: false,
    message: ""
  }
}
export const debugMode = process.env.REACT_APP_BUILD_ENV === 'prod' ? false : true;

// 🎯 Actions principales (header, boutons, etc.)
export const getActionLabelsByBrand = () => {
  const brand = getBrandConfig().name;
  const previousMonth = capitalizeFirstLetter(moment().subtract(1, 'month').format('MMMM'));

  switch (brand) {
    case 'verified-trainer':
      return {
        primaryActionLabel: '📚 Déclarer un cours',
        secondaryActionLabel: '➕👤 Ajouter un client',
        thirdryActionLabel: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
      };

    case 'declarons':
      return {
        primaryActionLabel: '📝 Déclarer une prestation',
        secondaryActionLabel: '➕👤 Ajouter un client',
        thirdryActionLabel: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
      };

    default:
      return {
        primaryActionLabel: '📚 Déclarer un cours',
        secondaryActionLabel: '➕👤 Ajouter un élève',
        thirdryActionLabel: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
      };
  }
};
// 📊 Sections / dashboards / listings
export const getDashboardLabelsByBrand = () => {
  const brand = getBrandConfig().name;
  const previousMonth = capitalizeFirstLetter(
    moment().subtract(1, 'month').format('MMMM')
  );

  switch (brand) {
    case 'verified-trainer':
      return {
        upcoming: `📅 ${capitalizeFirstLetter(getActivePrestationBrandConfig("s"))}  à venir`,
        currentMonth: `🔥 ${capitalizeFirstLetter(getActivePrestationBrandConfig("s"))} du mois`,
        history: '📚 Historique',
        drafts: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
        activeUsers: '👥 Mes clients',
        archivedUsers: '📦 Mes clients archivés',
      };

    case 'declarons':
      return {
        upcoming: '📅 Prestations à venir',
        currentMonth: '🔥 Prestations du mois',
        history: '📚 Historique',
        drafts: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
        activeUsers: '👥 Mes clients',
        archivedUsers: '📦 Mes clients archivés',
      };

    default:
      return {
        upcoming: `📅 ${capitalizeFirstLetter(getActivePrestationBrandConfig("s"))} à venir`,
        currentMonth: `🔥 ${capitalizeFirstLetter(getActivePrestationBrandConfig("s"))} du mois`,
        history: '📚 Historique',
        drafts: `📝 Brouillon des ${getActivePrestationBrandConfig("s")} - ${previousMonth}`,
        activeUsers: '👥 Mes élèves',
        archivedUsers: '📦 Mes élèves archivés',
      };
  }
};
export const displayHeadInfoDeclaration = (child: any, classes: any, courseType: any, duration: any) => {
  return (<>
    Vous procédez actuellement à la déclaration <strong className={`${classes.primaryText}`}> {courseType ? `de plusieurs cours 📚` : `${(getActivePrestationBrandConfig(' ponctuelle', "d'un ", "d'une ", " ponctuel"))} 📆`} </strong>pour votre {getActiveClientBrandConfig()} <strong className={`${classes.primaryText}`}> {fullNameChild(child?.childFirstName, child?.childLastName?.toUpperCase())}🎓</strong>
    {duration ? <>d'une durée de <strong className={`${classes.primaryText}`}> {Functions.renderDuration(duration * 2)} ⏰</strong></> : ""}
    {!child?.duration ? "." : <>, après avoir déjà déclaré <strong className={`${classes.primaryText}`}> {Functions.renderDuration(child?.duration)} ⏰</strong> des cours pour cet {getActiveClientBrandConfig()}.</>}
  </>)
}
export const displayUploadProof = (proofLenght: number, load: boolean, link: string, filePath: string, error: boolean, handleError: (value: any) => void, getFile: (value: File, error: boolean) => void, handleDelete: () => void, classes?: any) => {
  if (proofLenght > 0) {
    return (
      <div className={`flex items-center flex-col col-span-2 rounded-xl border-dashed py-2 border text-sm ${classes?.primaryBorder} ${classes?.background} /bg-[#F2F5FB] /order-last`} >
        {load ? "Chargement..." :
          <>
            <div className="flex items-center gap-x-4 gap-y-1 ">
              <a target="_blank"
                download href={link} className={`underline underline-offset-2 text-sm ${classes?.primaryText} ${classes.fontFamily}`}>{filePath}</a>
              <TaskAltSharpIcon className={` ${classes?.primaryText}`} fontSize='small' />
            </div>
            <Button onClick={() => handleDelete()} className={`mx-auto text-xs font-semibold underline ${classes?.text} ${classes?.fontFamily} w-max text-sm`}>Supprimer le fichier</Button>
          </>
        }
      </div>)
  } else {
    return (<div className='col-span-2'>
      <FileUploader
        classes='focus-within:bg-first lg:order-first'
        label="Preuve d'acompte (obligatoire)"
        hoverTitle=""
        children={
          <div className={`text-sm text-center flex items-center flex-col rounded-xl border-dashed py-2 border ${error ? `${classes?.errorBorder} ${classes.error}` : `${classes?.textBorder} ${classes?.text}`}  ${classes?.background}  ${classes?.fontFamily} /bg-[#F2F5FB]`}>
            {load ? "Chargement..." : <>
              <p className={`text-center ${classes?.fontFamily} text-sm`}>Veuillez insérer une preuve d'acompte (obligatoire) : ”PDF, PNG ou JPEG”</p>
              <p className={`font-semibold text-center ${classes?.fontFamily} text-sm`}>
                glissez ou choisissez <a href="" className={`font-bold text-sm ${classes?.fontFamily} ${classes?.primaryText}`}>un fichier</a>
              </p>
            </>}
          </div>
        }
        // onTypeError={(value: any) => setError((prev) => ({ ...prev, errorMsg: value }))}
        onTypeError={(value: any) => handleError(value)}
        handleChange={getFile}
        name="file"
        types={fileTypes}
      />
    </div>)
  }

}

/**
 * Affiche la liste des preuves de versement déjà ajoutées (avec suppression individuelle)
 * et un uploader tant que le nombre maximum de preuves n'est pas atteint.
 * Utilisé pour les déclarations de plusieurs cours (isPunctualMonth / courseType).
 */
export const displayUploadProofs = (
  proofs: any[],
  load: boolean,
  error: boolean,
  handleError: (value: any) => void,
  getFile: (value: File, error: boolean) => void,
  handleDelete: (index: number) => void,
  classes?: any,
  max: number = 8
) => {
  const list = Array.isArray(proofs) ? proofs : [];
  return (
    <div className="col-span-2 space-y-2">
      {list.length > 0 && (
        <div className={`flex flex-col gap-y-2 rounded-xl border-dashed py-2 px-3 border ${classes?.primaryBorder} ${classes?.background}`}>
          {list.map((proof: any, index: number) => (
            <div key={proof?.id ?? index} className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-x-2 min-w-0">
                <a
                  target="_blank"
                  rel="noreferrer"
                  download
                  href={proof?.contentUrl}
                  className={`underline underline-offset-2 truncate ${classes?.primaryText} ${classes?.fontFamily}`}
                >
                  {proof?.filePath || `Preuve ${index + 1}`}
                </a>
                <TaskAltSharpIcon className={`${classes?.primaryText} flex-shrink-0`} fontSize="small" />
              </div>
              <Button
                onClick={() => handleDelete(index)}
                className={`text-xs font-semibold underline ${classes?.text} ${classes?.fontFamily} w-max flex-shrink-0`}
              >
                Supprimer
              </Button>
            </div>
          ))}
        </div>
      )}
      {list.length < max && (
        <FileUploader
          classes="focus-within:bg-first lg:order-first"
          label="Preuve d'acompte (obligatoire)"
          hoverTitle=""
          children={
            <div className={` text-sm text-center flex items-center flex-col rounded-xl border-dashed py-2 border ${error ? `${classes?.errorBorder} ${classes.error}` : `${classes?.textBorder} ${classes?.text}`}  ${classes?.background}  ${classes?.fontFamily}`}>
              {load ? "Chargement..." : <>
                <p className={`text-center ${classes?.fontFamily} text-sm`}>Veuillez insérer une preuve d'acompte (obligatoire) : ”PDF, PNG ou JPEG” ({list.length}/{max})</p>
                <p className={`font-semibold text-center ${classes?.fontFamily} text-sm`}>
                  glissez ou choisissez <a href="" className={`font-bold ${classes?.fontFamily} ${classes?.primaryText} text-sm`}>un fichier</a>
                </p>
              </>}
            </div>
          }
          onTypeError={(value: any) => handleError(value)}
          handleChange={getFile}
          name="file"
          types={fileTypes}
        />
      )}
    </div>
  );
};

export const validateDeposit = (value: number, price: any, checked: boolean) => {
  if (checked && !value) {
    return {
      isValid: false,
      message: `Veuillez renseigner un montant d'acompte`,
    }
  }
  if (value > price) {
    return {
      isValid: false,
      message: `Vous ne pouvez sélectionner un montant d’acompte supérieur au reste à charge de votre ${getActiveClientBrandConfig()}`,
    }
  } if (value < 0) {
    return {
      isValid: false,
      message: `Le montant d'acompte ne peut pas être négatif`,
    }
  } else {
    return {
      isValid: true,
      message: "",
    };
  }
}
export const validateTva = (tva: number | string, tvaNumber: string) => {
  if (tva !== 0) {
    if (!tvaNumber) {
      return {
        isValid: false,
        message: "Veuillez entrer le numéro TVA",
      }
    } else if (tvaNumber.length < 13 || tvaNumber.length > 13) {
      return {
        isValid: false,
        message: "Le numéro Tva ne doit pas supérieur ou inférieur à 13 caracrères",
      }
    } else {
      return {
        isValid: true,
        message: "",
      }
    }
  } else {
    return {
      isValid: true,
      message: "",
    }
  }
};


//______________________-Tooltip and variable mui ______________________________________//
export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    borderRadius: 10,
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(2)
  },
}));
export const BpIcon2 = styled("span")(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 0 0 1px rgb(16 22 26 / 40%)"
      : "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: theme.palette.mode === "dark" ? "#394b59" : "#f5f8fa",
  backgroundImage:
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))"
      : "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
  ".Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2,
  },
  "input:hover ~ &": {
    backgroundColor: theme.palette.mode === "dark" ? "#30404d" : "#ebf1f5",
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background:
      theme.palette.mode === "dark"
        ? "rgba(57,75,89,.5)"
        : "rgba(206,217,224,.5)",
  },
}));
export const BpCheckedIcon2 = styled(BpIcon2)({
  backgroundColor: "#137cbd",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&::before": {
    display: "block",
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fillRule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "#106ba3",
  },
});
export const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: '#f5f8fa',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));

export const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: getBrandConfig().colors.primary,
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: getBrandConfig().colors.primary,
  },
});
export function BpRadio(props: RadioProps) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      {...props}
    />
  );
}
export const LightTooltipNoError = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(({ theme }) => ({
  [`& .${tooltipClasses?.tooltip}`]: {
    backgroundColor: "white",
    // backgroundColor: "#FF724F",
    color: "#FF724F",
    boxShadow: theme.shadows[1],
    fontSize: 14,
    fontFamily: "Nunito",
  },
}));
export const LightTooltipPaye = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(() => ({
  [`& .${tooltipClasses?.tooltip}`]: {
    backgroundColor: "white",
    color: "#4d4d4d",
    boxShadow: "7px 5px 20px  #E8FFEF",
    border: "1px solid  #E8FFEF",
    fontSize: 14,
    // fontFamily: "Nunito",
  },
}));
export const LightTooltipCours = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(() => ({
  [`& .${tooltipClasses?.tooltip}`]: {
    backgroundColor: "white",
    color: "#4d4d4d",
    boxShadow: "7px 5px 20px  #F4F4F8",
    border: "1px solid  #F4F4F8",
    fontSize: 14,
    // fontFamily: "Nunito",
  },
}));
export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses?.tooltip}`]: {
    backgroundColor: "#F2F9FF",
    color: "#4d4d4d",
    boxShadow: theme.shadows[1],
    fontSize: 10,
    // fontFamily: "Nunito",
  },
}));
//_______________________-Validation______________________________________//
export const validateCheckbox = (value: any) => {
  if (!value) {
    return {
      isValid: false,
      message: "Veuillez cocher cette case",
    };
  } else
    return {
      isValid: true,
      message: "",
    };
};
export const validateDateTime = (value: any, startTime: any, endTime: any) => {
  const hour = moment(endTime).hours() - moment(startTime).hours();
  const minute = (moment(endTime).minutes() - moment(startTime).minutes()) / 60;
  const duration = hour + minute;
  if (duration < value) {
    return {
      isValid: false,
      message:
        `Horaires de ${getActivePrestationBrandConfig()} non valide, veuillez sélectionner une plage horaire entre 00h01 et 23h59 sans chevauchement de journées.`
    };
  } else {
    return {
      isValid: true,
      message: "",
    };
  }
};
export const validateMultiDate = (date: any) => {
  let array: any = [];
  let endDay = moment(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)).format("YYYY-MM-DD")
  const nows = moment().format("YYYY-MM-DD");

  if (date?.length === 0) {
    return {
      error: true,
      message: "Veuillez renseigner une date",
    };
  } else if (date?.length === 1) {
    return {
      error: true,
      message: "Veuillez renseigner une date de fin",
    };
  } else if (date?.length === 2) {
    date.map((day: any) => {
      array.push(moment(day?.toDate?.()).format("YYYY-MM-DD"));
    });
    // if (array[1] < nows) {
    //   return {
    //     error: true,
    //     message:
    //       "La fin de la période des cours doit être aujourd'hui ou plus tard dans le mois",
    //   };
    // } else 
    if (moment(array[0]).month() !== moment(array[1]).month()) {
      return {
        error: true,
        message:
          "La déclaration doit concerner un seul mois, sans chevauchement entre deux mois. ",
      };
    } else
      if (array[1] === array[0]) {
        if (array[1] === endDay) {
          return {
            error: false,
            message: "",
          };
        } else {
          return {
            error: true,
            message:
              "Veuillez modifier la date de fin. Si vous souhaitez avoir un cours le même jour, veuillez déclarer un cours ponctuel",
          };
        }
      } else {
        return {
          error: false,
          message: "",
        };
      }
  } else {
    return {
      error: false,
      message: "",
    };
  }
};
export const validatePrice = (value: any, duration: any) => {

  if (value === undefined || value === "" || value === null || isNaN(value)) {
    return {
      isValid: false,
      message: "Entrez le prix",
      //   icon: <img src={errorIcon} alt="" className="w-4 h-4" />,
    };
  } else if (value < 6) {
    return {
      isValid: false,
      message:
        "Le prix par heure payé par votre client ne doit pas être inférieur à 6€",
      //   icon: <img src={errorIcon} alt="" className="w-4 h-4" />,
    };
  } else if (value > 138) {
    return {
      isValid: false,
      message:
        "Le prix par heure payé par votre client ne doit pas dépasser 138€",
      //   icon: <img src={errorIcon} alt="" className="w-4 h-4" />,
    };
  } else if (value === 0) {
    return {
      isValid: false,
      message: "Veuillez entrer le prix",
      //   icon: <img src={errorIcon} alt="" className="w-4 h-4" />,
    };
  } else if (Functions.priceTotalTTC(value * duration) > 990) {
    return {
      isValid: false,
      message:
        `Le montant total facturé de la prestation de votre ${getActiveClientBrandConfig()} ne doit pas depasser 990€. Sur le cours que vous êtes en train d'enregistrer, le montant total facturé est de ` +
        Functions.priceTotalTTC(value * duration).toFixed(2) + "€",
      //   icon: <img src={errorIcon} alt="" className="w-4 h-4" />,
    };
  } else {
    return {
      isValid: true,
      message: "",
    };
  }
};
export const validateTerms = (value: boolean) => {
  if (!value) {
    return {
      isValid: false,
      message: `Vous devez vous engager à respecter cette condition pour faire bénéficier de l'Avance Immédiate Crédit d'Impôt à votre ${getActiveClientBrandConfig()}`,
    };
  } else
    return {
      isValid: true,
      message: "",
    };
};
//-------------------déclaration des cours----------------------------------//

export const handleSubmitCourse = async (dataPunctual: DataPunctual) => {
  dataPunctual?.data?.setLoad(true);

  const coursePunctual = new PunctualCourseDashboard(
    dataPunctual?.course.forChild_id,
    dataPunctual?.course.chapter,
    dataPunctual?.course.address_id,
    dataPunctual?.course.subjectAsked_id,
    dataPunctual?.course.startDateTime,
    dataPunctual?.course.endDateTime,
    dataPunctual?.course.isRemote,
    dataPunctual?.course.teachr_id,
    dataPunctual?.course.isCourseInTheHour,
    dataPunctual?.course.duration,
    dataPunctual?.course.isDirectCourse,
    dataPunctual?.course.isFiscalReductionEnabled,
    dataPunctual?.course.paymentMean,
    dataPunctual?.course.aiciActivated,
    dataPunctual?.course.chapterExtraInformation,
    dataPunctual?.course.globalExtraInformation,
    dataPunctual?.course.isPunctualMonth ? dataPunctual?.course.isPunctualMonth : false,
  );
  coursePunctual.isPassedCourse = dataPunctual?.course.isPassedCourse;
  coursePunctual.brand = isBrandActive(ENV_DECLARONS) ? "declarons" : undefined
  // P1 – séances détaillées (cours mensuel) transmises au backend
  coursePunctual.sessions = dataPunctual?.course?.sessions ?? null;
  if (dataPunctual?.course?.deposit) {
    coursePunctual.deposit = dataPunctual?.course?.deposit;
    coursePunctual.depositDate = dataPunctual?.course?.depositDate;
    // attestation sur l'honneur : signée côté serveur à la création du cours
    coursePunctual.depositAttestationAccepted = dataPunctual?.course?.depositAttestationAccepted === true;
    if (dataPunctual?.course?.proofDeposit) {
      coursePunctual.proofDeposit = dataPunctual?.course?.proofDeposit;
    }
    if (dataPunctual?.course?.proofDeposits && dataPunctual?.course?.proofDeposits.length > 0) {
      coursePunctual.proofDeposits = dataPunctual?.course?.proofDeposits;
    }
  }
  // déroulement propre à ce cours (étape 1), indépendant de l'acompte :
  // null = description habituelle du prof
  coursePunctual.sessionDescriptionOverride = dataPunctual?.course?.sessionDescriptionOverride ?? null;

  try {

    const punctualCourse = await PunctualCourseRepository.createPunctualCourseByTeacher(coursePunctual);

    // P5 : preuve de déplacement (> 160 km) rattachée au cours dès sa création.
    // Un échec d'upload ne bloque pas la suite : le cours reste en attente
    // d'approbation admin et la preuve peut être re-soumise.
    if (dataPunctual?.data?.travelProofFile && punctualCourse?.id) {
      try {
        await TravelRepository.uploadTravelProof(punctualCourse.id, dataPunctual.data.travelProofFile);
        TravelStore.setProofFile(null);
      } catch (e) {
        console.warn("Upload de la preuve de déplacement échoué", e);
      }
    } else if (dataPunctual?.data?.travelProofFromSubscription && punctualCourse?.id) {
      // P6 : l'abonnement de transports en commun valide du prof est rattaché
      // comme preuve côté serveur, sans re-upload. Même filet de sécurité.
      try {
        await TransportSubscriptionRepository.attachToCourse(punctualCourse.id);
      } catch (e) {
        console.warn("Rattachement de l'abonnement de transport échoué", e);
      }
    }

    const proposals: DataProposalBis = {
      punctualCourse: punctualCourse,
      course: dataPunctual?.course,
      data: dataPunctual?.data
    }
    handleProposal(proposals)
  } catch (error: any) {
    dataPunctual?.data?.setLoad(false);
    dataPunctual?.data?.setLoad(false);
    if (dataPunctual?.data?.setClose) {
      dataPunctual?.data?.setClose();
    }
    if (dataPunctual?.data?.handleCloseEdit) {
      dataPunctual?.data?.handleCloseEdit()
    }
    if (dataPunctual?.data?.handleCloseDeclare) {
      dataPunctual?.data?.handleCloseDeclare()
    }

    const errorMessages: Record<string, string> = {
      ["teachr_account_not_active"]:
        "Votre compte n'est pas encore actif : terminez votre profil (statut auto-entrepreneur, conditions de service signées, code de validation) avant de déclarer un cours.",
      ["teachr_iban_missing"]:
        "Renseignez votre IBAN dans votre compte avant de déclarer un cours.",
      ["teachr_address_missing"]:
        "Renseignez votre adresse dans votre compte avant de déclarer un cours.",
      [APIErrorEnum.PUNCTUAL_COURSE_RANGE_NOT_CORRESPONDING]:
        "La durée du cours est supérieure à la plage horaire définie.",
      [APIErrorEnum.PUNCTUAL_COURSE_CLIENT_MUST_HAVE_AT_LEAST_ONE_ADDRESS]:
        "Vous devez définir au moins une adresse dans votre compte.",
      [APIErrorEnum.COURSE_PROPOSAL_TEACHR_HAS_ALREADY_A_COURSE_AT_THIS_TIME]:
        "Vous avez déjà un cours prévu à cette date.",
      [APIErrorEnum.PUNCTUAL_COURSE_PENDING_RESERVATIONS_LIMIT_REACHED]:
        "Vous avez déjà trop de demandes de cours en attente.",
      [APIErrorEnum.PUNCTUAL_COURSE_DURATION_LIMIT_MAX]:
        "La durée du cours ne peut pas dépasser les 12 heures.",
      [APIErrorEnum.PUNCTUAL_COURSE_DURATION_LIMIT_MIN]:
        "La durée du cours ne peut pas être inférieure à 1 heure.",
      [APIErrorEnum.PUNCTUAL_COURSE_IS_NOT_COURSE_IN_THE_HOUR]:
        "Il ne s'agit pas d'un cours dans l'heure.",
      [APIErrorEnum.PUNCTUAL_COURSE_IS_COURSE_IN_THE_HOUR]:
        "Il s'agit d'un cours dans l'heure.",
      [APIErrorEnum.PUNCTUAL_COURSE_DIRECT_COURSE_NOT_AUTHORIZED]:
        "Vous n'êtes pas autorisé à demander un cours directement à ce Teach'r.",
      [APIErrorEnum.PUNCTUAL_COURSE_MAX_COURSE_FOR_THE_MONTH]:
        "Vous avez atteint la limite de 8 cours par mois glissant. Pour réserver un nouveau cours, veuillez attendre que votre période mensuelle se renouvelle ou contactez-nous pour plus d'informations.",
      ["child_not_found"]: ` "${getActiveClientBrandConfig('', "L'", "Le ")} que vous avez sélectionné ne fais plus partie de votre liste ${getActiveClientBrandConfig()}. Merci de le modifier."`,
      // Attestation sur l'honneur d'acompte
      ["deposit_attestation_required"]:
        "Vous devez certifier sur l'honneur l'attestation d'acompte avant de valider.",
      ["proof_deposit_required"]:
        "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant de valider.",
      ["deposit_attestation_failed"]:
        "La génération de l'attestation d'acompte a échoué. Votre déclaration n'a pas été enregistrée, veuillez réessayer.",
      ["deposit_locked_urssaf"]:
        `L'acompte de ${getActivePrestationBrandConfig("", "ce ", "cette ")} a déjà été transmis à l'URSSAF et ne peut plus être modifié.`,
      // P4 : description générique obligatoire avant déclaration
      ["session_description_required"]:
        `Vous devez d'abord renseigner le déroulement habituel de vos ${getActivePrestationBrandConfig("s")} dans Paramètres, onglet Descriptions.`,
    };
    const message = errorMessages[error.description]
      || describeOverlapApiError(error.description)
      || "Une erreur est survenue. Nous vous invitons à recommencer la déclaration du cours. Si le problème persiste, veuillez vous déconnecter puis vous reconnecter afin de pouvoir continuer.";
    dataPunctual?.data?.dispatch({ type: SHOW_ERROR, payload: message, title: "Attention !" })
  }
};
//-----3
export const handleProposal = async (dataProposalBis: DataProposalBis) => {
  dataProposalBis?.data?.setLoad(true);
  if (dataProposalBis?.punctualCourse) {
    const punctualId = `/punctual_courses/${dataProposalBis?.punctualCourse.id}`;
    const proposal = new CourseProposal(
      dataProposalBis?.course.teachrId,
      punctualId,
      dataProposalBis?.course.startDateTime,
      dataProposalBis?.course.endDateTime,
      dataProposalBis?.course.teachrPrice,
      dataProposalBis?.course.description
    );
    proposal.isFromTeachr = true;

    const proposals: dataTryAccept = {
      proposal: proposal,
      data: dataProposalBis?.data,
      punctualCourseId: dataProposalBis?.punctualCourse.id
    }

    await handleCreateCourseProposal(proposals);
  }
}
//-----4
export const handleCreateCourseProposal = async (dataProposal: dataTryAccept) => {
  dataProposal?.data?.setLoad(true);
  try {
    const courseProposal = await CourseProposalRepository.createCourseProposal(dataProposal?.proposal);
    if (courseProposal.id) {
      handleCreateTryToAccept(dataProposal, courseProposal.id)
    }

  } catch (error: any) {
    dataProposal?.data?.setLoad(false);

    if (error._arrayInvalidAttributes.length > 0) {
      error._arrayInvalidAttributes.forEach((err: any) => {
        handleValidationError(err.description, dataProposal?.data?.dispatch)
      });
    }
    else if (error._description) {
      handleValidationError(error._description, dataProposal?.data?.dispatch);
    }
    if (dataProposal?.data?.setClose) {
      dataProposal?.data?.setClose();
    }
    if (dataProposal?.data?.handleCloseEdit) {
      dataProposal?.data?.handleCloseEdit()
    }
    if (dataProposal?.data?.handleCloseDeclare) {
      dataProposal?.data?.handleCloseDeclare()
    }
  }
};

const handleCreateTryToAccept = async (dataProposal: dataTryAccept, courseProposalId: number) => {
  const id = dataProposal?.data?.teachr?.id
  const config = dataProposal?.data?.config
  const oldCourseId = dataProposal?.data?.oldCourseId
  // const dataDispatchActive = {
  //   currentPage: 1,
  //   isData: false,
  //   global: { id, nameSearch: '', sort: { var: 0, sort: 1 } },
  // };
  try {
    const accept = await PunctualCourseRepository.tryAcceptPunctualCourseByTeachr(dataProposal?.data?.clientId, courseProposalId) as { id: number } | null;
    if (accept && typeof accept === "object") {

      dataProposal?.data?.dispatch({
        type: RESET_END_DECLARE_COURSE,
        payload: null,
      });
      if (dataProposal?.data?.isCoursePass) {
        try {
          const coursePassed = { isPassedCourse: true };
          const course = await PunctualCourseRepository.setCoursePassed(dataProposal?.data?.punctualId, coursePassed)

          if (course && dataProposal?.data?.type && config) {
            const dataDispatch = {
              [config.pageKey]: 0,
              [config.limitKey]: 10,
              isData: false,
              global: { id, nameSearch: '', sort: { var: 0, sort: 1 } },
            }

            dataProposal?.data?.dispatch({ type: DELETE_COURSE, payload: { id: oldCourseId, clientId: dataProposal?.data?.clientId, courseType: REDUX_TYPE_MAP[dataProposal?.data?.type as keyof typeof REDUX_TYPE_MAP] } });
            store.dispatch(config.action(dataDispatch));
            // store.dispatch(getNextCourse(dataProposal?.data?.dataDispatch));
            // store.dispatch(getActiveCourse(dataProposal?.data?.dataDispatch ? dataProposal?.data?.dataDispatch : dataDispatchActive));
            // store.dispatch(getPendingCourse(dataProposal?.data?.dataDispatch ? dataProposal?.data?.dataDispatch : dataDispatchActive));
            dataProposal?.data?.dispatch({
              type: "SHOW_SUCCESS",
              payload: "Votre cours a bien été validé. Il apparait déjà dans votre tableau de bord !",
            });
            dataProposal?.data?.setLoad(false);
            dataProposal?.data?.handleCloseEdit()
            dataProposal?.data?.handleCloseDeclare()
          }
        } catch (error) {
          console.warn(error)
          dataProposal?.data?.setLoad(false);
          dataProposal?.data?.handleCloseEdit()
          dataProposal?.data?.handleCloseDeclare()
        }
      } else {
        dataProposal?.data?.dispatch({
          type: SET_COURSE_DECLARATION_INFO,
          payload: {
            step: dataProposal?.data?.value,
            firstCourse: dataProposal?.data?.teachr?.isFirstCourse,
            cours: dataProposal?.data?.isCours,
            // P2 – permet à l'écran de fin d'afficher le devis généré
            declaredCourseId: dataProposal?.punctualCourseId ?? null
          },
        });
        dataProposal?.data?.handleChangeStep(5);
      }
    }

  } catch (error) {
    // console.warn(error, 2)
    dataProposal?.data?.setLoad(false);
    const message = "Une erreur est survenue. Nous vous invitons à recommencer la déclaration du cours. Si le problème persiste, veuillez vous déconnecter puis vous reconnecter afin de pouvoir continuer.";
    dataProposal?.data?.dispatch({ type: SHOW_WARNING, payload: message, title: "Attention !" })
  }
}

//_______________________-Create image______________________________________//
export const createImage = (file: File) => {
  ImageRepository.createImage(new File([file], "proof-deposit", { type: "application/pdf" })).then((response) => {
    // setForm((prev) => ({ ...prev, proofDeposit: response["@id"] }));
  }).catch((error: any) => {
    console.error(
      "Error updating teachr:",
      JSON.parse(JSON.stringify(error))._description
    );
    // Invalid filename provided
    if (JSON.parse(JSON.stringify(error))._description === "Invalid filename provided") {
      console.error(
        JSON.parse(JSON.stringify(error))._description || "An error occurred while uploading the file."
      );
    } else {
      console.error(JSON.parse(JSON.stringify(error)));
    }
    // setOpen((prev) => ({ ...prev, loading: false }));
  })
}


//_______________________-Hooks______________________________________//
export const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
export const updatePaginationState = (updateData: updatePaginationInterface) => {
  if (updateData.data) {
    // updateData.setChild(updateData.data.clients || updateData.data.data);
    if (updateData.setCurrentPage) {
      updateData.setCurrentPage(updateData.data.page);
    }

    // updateData.setNextPage?.(updateData.data.nextPage);
    // updateData.setPrevPage?.(updateData.data.previousPage);
    // updateData.setTotalPage?.(updateData.data.totalPages);
    updateData.paginateFunc?.(updateData.data.page);
    updateData.setTotalItems?.(updateData.data.totalItems)
    updateData.setLoading?.(false);
  }
};
export const updatePaginationStateBis = (updateData: updatePaginationInterfaceBis) => {
  if (updateData.data) {
    updateData.setCurrentPage(updateData.data.page);
    updateData.paginateFunc(updateData.data.page);
    updateData.setTotalItems(updateData.data.totalItems)
    updateData.setLoading(false);
  }
};
//_______________________-Alert error______________________________________//

export const displayError = (error: boolean, msg: string, classes?: any): any => {
  return (<strong className={`${error ? 'block' : 'hidden'} ${classes?.error} text-sm italic`}>{msg}</strong>)
}
export const handleValidationError = (description: string, dispatch: Dispatch<AnyAction>) => {
  const messages: Record<string, string> = {
    [APIErrorEnum.COURSE_PROPOSAL_TEACHR_HAS_ALREADY_A_COURSE_AT_THIS_TIME]:
      "Vous avez déjà un cours prévu pour ce créneau horaire.",
    [APIErrorEnum.COURSE_PROPOSAL_ALREADY_APPLIED]:
      "Vous avez déjà un cours prévu pour ce cours",
    [APIErrorEnum.COURSE_PROPOSAL_DURATION_NOT_CORRESPOND]:
      "La durée du cours ne correspond pas à la plage horaire définie, veuillez modifier la durée ou la plage horaire prévu pour ce cours.",
    "This value should be between 6 and 250.":
      "Le prix par heure doit se situer dans une fourchette allant de 11 à 250 euros.",
    "This value should be between 5 and 23.":
      "Le prix par heure doit se situer dans une fourchette allant de 9 à 23 euros.",
    "The end datetime must be within the next month.":
      "La déclaration des cours ne doit pas excéder une durée d'un mois.",
    "The start datetime must be in the future.":
      "Veuillez sélectionner une date et une heure futures pour la fin du cours, car celles choisies sont déjà passées.",
    "The end datetime must be in the future.":
      "Veuillez sélectionner une date et une heure futures pour la fin du cours, car celles choisies sont déjà passées.",
    // Attestation sur l'honneur d'acompte (modification d'un cours)
    ["deposit_attestation_required"]:
      "Vous devez certifier sur l'honneur l'attestation d'acompte avant d'enregistrer.",
    ["proof_deposit_required"]:
      "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant d'enregistrer.",
    ["deposit_attestation_failed"]:
      "La génération de l'attestation d'acompte a échoué. La modification n'a pas été enregistrée, veuillez réessayer.",
    ["deposit_locked_urssaf"]:
      `L'acompte de ${getActivePrestationBrandConfig("", "ce ", "cette ")} a déjà été transmis à l'URSSAF et ne peut plus être modifié.`,

  };

  const message = messages[description]
    || describeOverlapApiError(description)
    || "Une erreur est survenue. Nous vous invitons à recommencer la déclaration du cours. Si le problème persiste, veuillez vous déconnecter puis vous reconnecter afin de pouvoir continuer.";
  dispatch({ type: SHOW_WARNING, payload: message, title: "Attention !" })
};
export const dialogUpdate = (data: dataModel) => {
  return (
    <BootstrapDialog
      open={data.open}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle className="lg:text-xl md:text-lg text-base text-first font-extrabold text-center uppercase">{data.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description" className="lg:text-lg md:text-base text-sm text-normal">
          {data.text}
        </DialogContentText>
        <div className={`flex flex-col gap-y-4 pt-4 ${data.customInput} }`}>
          <div className='relative w-full'>
            <p className={`${data.error ? 'block' : 'hidden'} text-third text-base md:text-base`}>{data.errorMsg}</p>
            <FormInput label="Chiffre d'affaires" type='number' value={data.data} onChange={(e) => data.getData(e.target.value)} placeholder='Ex : 1500 €' />
          </div>
        </div>
      </DialogContent>
      <DialogActions className="lg:text-lg md:text-base text-sm font-bold">
        <button className="p-1 rounded-ful w-1/2 text-normal "
          onClick={data.handleClose}
        >{data.cancel}</button>
        <button disabled={data?.load} className="p-1 rounded-full bg-first text-white w-1/2" onClick={data.handleSubmit}>{data?.load ? "..." : data.valid}</button>

      </DialogActions>
    </BootstrapDialog>)
}
//__________________Formatter_____________________________//
export const formatPhoneNumber = (value: string) => {
  // Supprimer tous les caractères non numériques sauf le "+"
  let cleaned = value?.replace(/[^\d+]/g, "").trim();

  // Vérifier si le numéro commence par "+33"
  if (cleaned?.startsWith("+33")) {
    cleaned = cleaned.replace("+33", "").trim(); // Retirer +33 temporairement
    cleaned = cleaned.replace(/^0/, ""); // Supprimer le 0 s'il est présent après +33

    // Ajouter les espaces au bon endroit
    return "+33 " + cleaned.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  }

  // Vérifier si le numéro commence par "0" et le convertir en format +33
  if (cleaned?.startsWith("0")) {
    cleaned = cleaned.replace(/^0/, ""); // Supprimer le 0 initial

    // Ajouter +33 et formater le numéro
    return "+33 " + cleaned.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  }

  return cleaned; // Retourner le numéro brut si ce n'est pas un format +33 ou 0
};
export const capitalizeFirstLetter = (str: string | any) => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  } else {
    return ""; // Ou un comportement par défaut selon vos besoins
  }
};
export const formatText = (text: string, error: any, textError: string) => {
  return (
    <div className="lg:text-lg md:text-base text-sm font-nunito">
      {
        !error ? <span>{text}</span> :
          <LightTooltipNoError title={textError} className="font-nunito cursor-auto">
            <div>
              <span className={!error ? "text-[#FF724F]" : "text-normal"} >{text}</span>
            </div>
          </LightTooltipNoError>
      }
    </div>)
}
export const formatPhone = (phone: string | null | undefined) => {
  if (phone) {
    return '0' + phone.slice(3, 4) + '-' + phone.slice(4, 6) + '-' + phone.slice(6, 8) + '-' + phone.slice(8, 10) + '-' + phone.slice(10, 12)
  }
  return
}
export const fullNameChild = (firstName: string, lastName: string) => {
  return `${capitalizeFirstLetter(firstName)} ${lastName?.toUpperCase()}`
}
export const removeExtraSpace = (s: any) => s.trim().split(/ +/).join("").toLowerCase();
export function extractDateTimeFromString(text: any) {
  const regex = /(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2})/;
  const match = text?.match(regex);

  if (match) {
    return match[0];
  }

  return null;
}
export function convertDateTimeFormat(dateTimeStr: any) {
  if (dateTimeStr) {
    const [date, time] = dateTimeStr?.split(" ");
    const [day, month, year] = date?.split("/");
    const isoFormattedStr = `${year}-${month}-${day}T${time}`;

    // Création de l'objet Date
    const dateObj = new Date(isoFormattedStr);

    return dateObj;
  }
}
export const translateDay = (day: string) => {
  const translate = days.filter(
    (item: any) => item.en.toLowerCase() === day.toLowerCase()
  );
  if (translate.length >= 0) {
    return translate[0].fr;
  }
  return "";
};
export const getMinute = (value: number): number => {
  if (value % 5 !== 0) {
    value += (5 - (value % 5));
  }
  return value
}
//_______________Render______________________________///
export const renderTitle = (text: string, msg: string, classes?: any) => {
  return (
    <div className="flex items-center gap-x-2">
      <p>{text}</p>
      <LightTooltipCours className={`${classes?.fontFamily}`} title={msg} >
        <HelpOutlineOutlinedIcon className="cursor-pointer" fontSize="small" />
        {/* <InfoOutlinedIcon className="cursor-pointer" /> */}
      </LightTooltipCours>
    </div>
  )
}
export const renderName = (fullName: string, brandConfig: any, initFullName?: string) => {
  return (
    <div className="flex items-center gap-x-2 w-full ">
      <Avatar
        sx={{ bgcolor: brandConfig.colors.background, color: brandConfig.colors.primary, border: `2px solid ${brandConfig.colors.primary}` }}
        className="flex w-7 h-7 font-bold text-sm"
      >
        {/* {initFullName} */}
      </Avatar>
      <div className="/text-pretty break-words whitespace-pre-wrap">
        {fullName}
      </div>
    </div>
  );
};
export const renderContentTd = (text: string, icon: boolean, msg?: string, iconTest?: any, classes?: any) => {

  return (
    <Tooltip title={msg} className="font-nunito cursor-auto">
      <div className='flex items-center gap-x-2 '>
        {iconTest ? iconTest : (icon ? <QueryBuilderSharpIcon className={`w-5 h-5 mr-2 ${classes?.primaryText} `} /> : <img src={iconMoney} alt="renumeration image" className={`"w-auto h-8 mr-3"`} />)}
        <div className={`text-first} text-center`}>
          {text}
        </div>
      </div>
    </Tooltip>
  )
}

export const renderActions = (paramActions: paramActionsInterface) => {
  if (paramActions.firstCondition) {
    return (
      <Tooltip title={TdpDescEnum.STATUS_6_ACTIVE_DEFAULT} >
        <Button >
          <HelpOutlineOutlinedIcon className="text-first" />
        </Button>
      </Tooltip>
    )
  }
  return (
    <>
      <Button
        id="basic-button"
        aria-controls={paramActions.open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={paramActions.open ? 'true' : undefined}
        onClick={paramActions.handleClick}
      >
        <MoreHorizSharpIcon />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={paramActions.anchorEl}
        open={paramActions.open}
        onClose={paramActions.handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {paramActions.listItems?.map((items: lisActionInterface, key: number) => (
          <MenuItem key={key} onClick={items.funct} className={`${items.class}`}>
            <ListItemIcon>{items.icon} </ListItemIcon>
            {items.isCopy ?
              <div onCopy={items.funct} className="font-nunito font-semibold">{items.title}</div> :
              <span className="font-nunito font-semibold">{items.title}</span>
            }
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
export const spanDownLoad = (punctual: any) => {
  // Verified Trainer : le coach voit aussi la facture envoyée au client (facture Verified Trainer SAP, ou facture
  // globale pour les cours antérieurs au 1er novembre 2026). Chez Teach'r, seule sa facture.
  const isVt = isBrandActive(ENV_VERIFIED_TRAINER);
  const showClientInvoice = isVt && !!punctual?.invoiceRecap;
  // Anciens cours Verified Trainer : facture des frais de gestion, comme dans l'e-mail de l'époque. La sous-traitance
  // des nouveaux cours (Verified Trainer vers Verified Trainer SAP) ne concerne pas le coach.
  const relationUrl = String(punctual?.invoiceRelation ?? "");
  const showFeesInvoice = isVt && !!punctual?.invoiceRelation && !relationUrl.includes("SOUS-TRAITANCE") && !relationUrl.includes("VT-ST-");
  return (
    <span className="relative inline-flex flex-wrap gap-x-4 gap-y-1 /px-3 py-1 font-semibold text-normal leading-tight">
      <span className="absolute inset-0 flex opacity-50 cursor-pointer" />
      {/* <KeyboardDoubleArrowRightIcon sx={{ width: 17 }} /> */}
      <a
        target="_blank"
        download
        href={punctual?.invoice}
        className="relative /text-xs /text-normal text-first underline"
      >
        {isVt ? "Télécharger votre facture" : "Télécharger la facture"}
        {/* <ArrowCircleDownIcon sx={{ width: 17 }} /> */}
      </a>
      {showClientInvoice && (
        <a
          target="_blank"
          download
          href={punctual.invoiceRecap}
          className="relative /text-xs /text-normal text-first underline"
        >
          Télécharger la facture envoyée au client
        </a>
      )}
      {showFeesInvoice && (
        <a
          target="_blank"
          download
          href={punctual.invoiceRelation}
          className="relative /text-xs /text-normal text-first underline"
        >
          Télécharger la facture des frais de gestion
        </a>
      )}
    </span>)
}
// État de la facture renvoyé par l'API (UrssafServiceStatus::INVOICE_STATE_* côté backend)
export const INVOICE_STATE = {
  SENT: "ENVOYEE",
  PAID: "PAYEE",
  UNPAID: "IMPAYEE",
  CANCELED: "ANNULEE",
  REGULARIZED: "REGULARISEE",
};

export const showDownloadButton = (cours: any) => {
  // API sans invoiceState (backend pas encore déployé) : ancien comportement basé sur le statut URSSAF
  if (cours?.invoiceState !== undefined) {
    switch (cours.invoiceState) {
      case INVOICE_STATE.SENT:
      case INVOICE_STATE.PAID:
      case INVOICE_STATE.REGULARIZED:
        return cours?.invoice ? spanDownLoad(cours) : null;
      case INVOICE_STATE.UNPAID:
        return cours?.invoice ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            {spanDownLoad(cours)}
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Impayée</span>
          </span>
        ) : null;
      case INVOICE_STATE.CANCELED:
        return (
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700">Facture annulée</span>
            {cours?.creditNote && (
              <a target="_blank" rel="noreferrer" download href={cours.creditNote} className="font-semibold text-first underline">
                Télécharger la facture d'avoir
              </a>
            )}
          </span>
        );
      default:
        return null;
    }
  }
  switch (cours?.urssafServiceStatus) {
    case status.STATUS_PAID.status:
      return spanDownLoad(cours);
    case status.STATUS_VALIDATED.status:
      return spanDownLoad(cours);
    case status.STATUS_REFUSAL_OF_DIRECT_DEBIT.status:
      return spanDownLoad(cours);
    case status.STATUS_COVERED.status:
      return spanDownLoad(cours);
    default:
      return null;
    // (
    //   <span className="relative inline-block /px-3 py-1 font-semibold text-normal leading-tight">
    //     {/* <KeyboardDoubleArrowRightIcon sx={{ width: 17 }} /> */}
    //     <span className="absolute inset-0 flex opacity-50 cursor-not-allowed" />
    //     Non disponible
    //   </span>
    // );
  }
}
//___________________Display STATUT_______________________//
export const statusModel = (text: string, title: string, color?: string, icon?: any, isStatus?: boolean, classes?: any) => {
  if (isStatus) {
    return (
      <span className={`${classes?.fontFamily}`}>
        {text}
      </span>
    )
  } else {
    return (
      <Tooltip title={title} placement="top" arrow>
        <Chip
          icon={icon ?? undefined}
          label={text}
          size="small"
          className={color ?? `${classes?.text} ${classes?.textBorder} bg-[#F4F4F8]`}
          sx={{
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace',
            border: '1px solid',
            ...(!color && classes?.fontFamily ? { fontFamily: classes.fontFamily } : {}),
          }}
        />
      </Tooltip>
    )
  }
}
export const displayStatutTdp = (status: string, isStatus?: boolean, classes?: any, error?: string) => {

  switch (status) {
    case TdpEnum.STATUS_1_BLOQUE:
      return statusModel(TdpEnumColor.STATUS_1_BLOQUE.title, TdpDescEnum.STATUS_1_BLOQUE, TdpEnumColor.STATUS_1_BLOQUE.color, null, isStatus, classes)
    case TdpEnum.STATUS_2_EC:
      return statusModel(TdpEnumColor.STATUS_2_EC.title, TdpDescEnum.STATUS_2_EC, TdpEnumColor.STATUS_2_EC.color, null, isStatus, classes)
    case TdpEnum.STATUS_3_NF:
      return statusModel(TdpEnumColor.STATUS_3_NF.title, TdpDescEnum.STATUS_3_NF, TdpEnumColor.STATUS_3_NF.color, null, isStatus, classes)
    case TdpEnum.STATUS_3_VALIDE:
      return statusModel(TdpEnumColor.STATUS_3_VALIDE.title, TdpDescEnum.STATUS_3_VALIDE, TdpEnumColor.STATUS_3_VALIDE.color, null, isStatus, classes)
    case TdpEnum.STATUS_4_ERROR:
      return statusModel(TdpEnumColor.STATUS_4_ERROR.title, error ?? TdpDescEnum.STATUS_4_ERROR, TdpEnumColor.STATUS_4_ERROR.color, null, isStatus, classes)
    case TdpEnum.STATUS_5_MANDAT_ECHU:
      return statusModel(TdpEnumColor.STATUS_5_MANDAT_ECHU.title, TdpDescEnum.STATUS_5_MANDAT_ECHU, TdpEnumColor.STATUS_5_MANDAT_ECHU.color, null, isStatus, classes)
    default:
      return statusModel(TdpEnumColor.STATUS_5_DEFAULT.title, TdpDescEnum.STATUS_5_DEFAULT, TdpEnumColor.STATUS_5_DEFAULT.color, null, isStatus, classes)
  }
};
export const displayStatutSponsorship = (text: string) => {
  switch (text) {
    case SponsorshipEnum.STATUS_1_SEND:
      return statusModel(SponsorshipEnumColor.STATUS_1_SEND.title, SponsorshipDescEnum.STATUS_1_SEND, SponsorshipEnumColor.STATUS_1_SEND.color)
    case SponsorshipEnum.STATUS_2_REGISTER:
      return statusModel(SponsorshipEnumColor.STATUS_2_REGISTER.title, SponsorshipDescEnum.STATUS_2_REGISTER, SponsorshipEnumColor.STATUS_2_REGISTER.color)
    case SponsorshipEnum.STATUS_3_PROC_AE:
      return statusModel(SponsorshipEnumColor.STATUS_3_PROC_AE.title, SponsorshipDescEnum.STATUS_3_PROC_AE, SponsorshipEnumColor.STATUS_3_PROC_AE.color)
    case SponsorshipEnum.STATUS_3_PROC_SAP:
      return statusModel(SponsorshipEnumColor.STATUS_3_PROC_SAP.title, SponsorshipDescEnum.STATUS_3_PROC_SAP, SponsorshipEnumColor.STATUS_3_PROC_SAP.color)
    case SponsorshipEnum.STATUS_4_OK_PROC_AE:
      return statusModel(SponsorshipEnumColor.STATUS_4_OK_PROC_AE.title, SponsorshipDescEnum.STATUS_4_OK_PROC_AE, SponsorshipEnumColor.STATUS_4_OK_PROC_AE.color)
    case SponsorshipEnum.STATUS_4_OK_PROC_SAP:
      return statusModel(SponsorshipEnumColor.STATUS_4_OK_PROC_SAP.title, SponsorshipDescEnum.STATUS_4_OK_PROC_SAP, SponsorshipEnumColor.STATUS_4_OK_PROC_SAP.color)
    case SponsorshipEnum.STATUS_5_OK_CONDITION:
      return statusModel(SponsorshipEnumColor.STATUS_5_OK_CONDITION.title, SponsorshipDescEnum.STATUS_5_OK_CONDITION, SponsorshipEnumColor.STATUS_6_ACCOUNT_VALID.color)
    case SponsorshipEnum.STATUS_6_ACCOUNT_VALID:
      return statusModel(SponsorshipEnumColor.STATUS_6_ACCOUNT_VALID.title, isBrandActive(ENV_VERIFIED_TRAINER) ? "Compte validé" : SponsorshipDescEnum.STATUS_6_ACCOUNT_VALID, SponsorshipEnumColor.STATUS_6_ACCOUNT_VALID.color)
    case SponsorshipEnum.STATUS_7_ADD_STUDENT:
      return statusModel(SponsorshipEnumColor.STATUS_7_ADD_STUDENT.title, SponsorshipDescEnum.STATUS_7_ADD_STUDENT, SponsorshipEnumColor.STATUS_7_ADD_STUDENT.color)
    case SponsorshipEnum.STATUS_8_FIRST_COURSE:
      return statusModel(SponsorshipEnumColor.STATUS_8_FIRST_COURSE.title, SponsorshipDescEnum.STATUS_8_FIRST_COURSE, SponsorshipEnumColor.STATUS_8_FIRST_COURSE.color)
    case SponsorshipEnum.STATUS_9_COMPLET:
      return statusModel(SponsorshipEnumColor.STATUS_9_COMPLET.title, SponsorshipDescEnum.STATUS_9_COMPLET, SponsorshipEnumColor.STATUS_9_COMPLET.color)
    default:
      break;
  }
}
export const displayStatutLeft = (status: statusInterface, classes: any) => {
  const stepClass = (active: boolean) =>
    `${active ? `${classes?.background} ${classes?.primaryBorder}` : "border-grey"} border  ${classes?.primaryText} rounded-2xl w-6 h-6 text-center pt-1`;
  const textClass = (active: boolean) =>
    `${active ? `${classes?.primaryText}` : "text-grey"} text-xs text-center -translate-y-5`;
  const iconClass = (active: boolean) =>
    `${active ? `${classes?.primaryText} -translate-y-5` : "text-grey -translate-y-5"}`;

  const displayMobile = () => {
    if (!!status.status) {
      return statusModel("Envoyé", "", TdpEnumColor.STATUS_5_DEFAULT.color, null)
    } else {
      return statusModel("En cours", "", TdpEnumColor.STATUS_2_EC.color, null)
    }
  }

  return (
    <>
      <div className="lg:flex hidden items-center w-full pt-2">
        {/* Step 1: Envoyé */}
        <div className={stepClass(true)}>
          <p className={textClass(true)}>Envoyé</p>
          <p className="absolute w-2 text-white -translate-y-5">
            <CheckOutlinedIcon className={`${classes?.primaryText}`} />
          </p>
        </div>

        {/* Separator */}
        <div className={`border-b-2 ${classes?.primaryBorder} flex-grow`} />

        {/* Step 2: Complété ou En cours */}
        <div className={stepClass(!!status.status)}>
          <p className={`${textClass(!!status.status)} w-14 -translate-x-4`}>
            {status.status ? "Complété" : "En cours"}
          </p>
          <p className={iconClass(!!status.status)}>
            {status.status ? <CheckOutlinedIcon /> : 2}
          </p>
        </div>

        {/* Separator */}
        <div className={`${status.status ? classes?.primaryBorder : "border-grey"} border-b-2 flex-grow`} />

        {/* Step 3: Validé */}
        <div className={stepClass(status.valid)}>
          <p className={`${textClass(status.valid)} /-translate-x-1`}>Validé</p>
          <p className={iconClass(status.valid)}>
            {status.valid ? <CheckOutlinedIcon /> : 3}
          </p>
        </div>
      </div>
      <div className="lg:hidden">
        {displayMobile()}
      </div>
    </>
  );
};
/** Statut d'un cours résolu en texte, description, couleur et icône (sans rendu). */
export interface CourseStatusModel {
  text: string;
  title: string;
  color?: string;
  icon?: any;
}

/** Le cours attend une décision de l'équipe ou a été rejeté : ce statut prime sur tout (date, Urssaf). */
export const hasBlockingDeclarationStatus = (declarationStatus?: string | null): boolean =>
  declarationStatus === DeclarationStatusEnum.PENDING_APPROVAL || declarationStatus === DeclarationStatusEnum.REJECTED;

export const resolveStatutCourse = (status: string, description: string, defaulDescription: string, isOver?: boolean, declarationStatus?: string | null, declarationStatusReason?: string | null): CourseStatusModel => {

  // P5 – statut de déclaration selon la distance prof/client.
  // pending_approval et rejected priment toujours : le cours n'est pas déclaré
  // à l'Urssaf tant que l'admin n'a pas statué, le statut de paiement n'a donc
  // pas de sens pour le prof.
  if (declarationStatus === DeclarationStatusEnum.PENDING_APPROVAL) {
    const s = DeclarationStatusEnumColor.PENDING_APPROVAL;
    // Le motif posé par le serveur prime sur le texte générique « > 160 km » :
    // une adresse identique à celle du client part aussi en approbation.
    const reason = (declarationStatusReason ?? "").trim();
    const description = reason
      ? `${DeclarationStatusEnumColor.PENDING_APPROVAL.descriptionWithReason(reason)}`
      : s.description;
    return { text: s.title, title: description, color: s.color, icon: s.icon };
  }
  if (declarationStatus === DeclarationStatusEnum.REJECTED) {
    const s = DeclarationStatusEnumColor.REJECTED;
    const reason = declarationStatusReason ? `${s.description} Motif : ${declarationStatusReason}` : s.description;
    return { text: s.title, title: reason, color: s.color, icon: s.icon };
  }
  // pending_verification (stock de l'ancienne règle 60–160 km) : le cours est
  // déclaré — dès qu'un statut Urssaf existe, c'est lui qui s'affiche.
  if (declarationStatus === DeclarationStatusEnum.PENDING_VERIFICATION && status === null) {
    const s = DeclarationStatusEnumColor.PENDING_VERIFICATION;
    return { text: s.title, title: s.description, color: s.color, icon: s.icon };
  }

  if (status === null) {
    if (isOver) {
      return { text: UrssafServiceStatEnumColor.EN_COURS.title, title: UrssafServiceStatusDescription.EN_COURS, color: UrssafServiceStatEnumColor.EN_COURS.color, icon: UrssafServiceStatEnumColor.EN_COURS.icon }
    } else {
      return { text: UrssafServiceStatEnumColor.COURS_PREVU.title, title: UrssafServiceStatusDescription.COURS_PREVU, color: UrssafServiceStatEnumColor.COURS_PREVU.color, icon: UrssafServiceStatEnumColor.COURS_PREVU.icon }
    }
  }

  switch (status) {
    //10
    case UrssafServiceStatEnum.STATUS_10_INTEGRATED:
      return { text: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.title, title: description ?? UrssafServiceStatusDescription.STATUS_INTEGRATED, color: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.color, icon: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.icon }
    case UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.value:
      return { text: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.title, title: description ?? UrssafServiceStatusDescription.STATUS_INTEGRATED, color: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.color, icon: UrssafServiceStatEnumColor.STATUS_10_INTEGRATED.icon }
    // 20
    case UrssafServiceStatEnum.STATUS_20_WAITING:
      return { text: UrssafServiceStatEnumColor.STATUS_20_WAITING.title, title: description ?? UrssafServiceStatusDescription.STATUS_WAITING_FOR_VALIDATION, color: UrssafServiceStatEnumColor.STATUS_20_WAITING.color, icon: UrssafServiceStatEnumColor.STATUS_20_WAITING.icon }
    case UrssafServiceStatEnumColor.STATUS_20_WAITING.value:
      return { text: UrssafServiceStatEnumColor.STATUS_20_WAITING.title, title: description ?? UrssafServiceStatusDescription.STATUS_WAITING_FOR_VALIDATION, color: UrssafServiceStatEnumColor.STATUS_20_WAITING.color, icon: UrssafServiceStatEnumColor.STATUS_20_WAITING.icon }
    // 30
    case UrssafServiceStatEnum.STATUS_30_VALIDATED:
      return { text: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.title, title: description ?? UrssafServiceStatusDescription.STATUS_VALIDATED, color: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.color, icon: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.icon }
    case UrssafServiceStatEnumColor.STATUS_30_VALIDATED.value:
      return { text: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.title, title: description ?? UrssafServiceStatusDescription.STATUS_VALIDATED, color: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.color, icon: UrssafServiceStatEnumColor.STATUS_30_VALIDATED.icon }
    // 40
    case UrssafServiceStatEnum.STATUS_40_REFUSAL:
      return { text: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.title, title: description ?? UrssafServiceStatusDescription.STATUS_REFUSED, color: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.color, icon: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.icon }
    case UrssafServiceStatEnumColor.STATUS_40_REFUSAL.value:
      return { text: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.title, title: description ?? UrssafServiceStatusDescription.STATUS_REFUSED, color: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.color, icon: UrssafServiceStatEnumColor.STATUS_40_REFUSAL.icon }
    case UrssafServiceStatEnum.STATUS_50_COLLECTED:
      return { text: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.title, title: description ?? UrssafServiceStatusDescription.STATUS_COLLECTED, color: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.color, icon: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.icon }
    case UrssafServiceStatEnumColor.STATUS_50_COLLECTED.value:
      return { text: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.title, title: description ?? UrssafServiceStatusDescription.STATUS_COLLECTED, color: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.color, icon: UrssafServiceStatEnumColor.STATUS_50_COLLECTED.icon }
    case UrssafServiceStatEnum.STATUS_60_REFUSAL_OF_DIRECT_DEBIT:
      return { text: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.title, title: description ?? UrssafServiceStatusDescription.STATUS_REFUSAL_OF_DIRECT_DEBIT, color: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.color, icon: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.icon }
    case UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.value:
      return { text: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.title, title: description ?? UrssafServiceStatusDescription.STATUS_REFUSAL_OF_DIRECT_DEBIT, color: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.color, icon: UrssafServiceStatEnumColor.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.icon }
    case UrssafServiceStatEnum.STATUS_70_PAID:
      return { text: UrssafServiceStatEnumColor.STATUS_70_PAID.title, title: description ?? UrssafServiceStatusDescription.STATUS_PAID, color: UrssafServiceStatEnumColor.STATUS_70_PAID.color, icon: UrssafServiceStatEnumColor.STATUS_70_PAID.icon }
    case UrssafServiceStatEnumColor.STATUS_70_PAID.value:
      return { text: UrssafServiceStatEnumColor.STATUS_70_PAID.title, title: description ?? UrssafServiceStatusDescription.STATUS_PAID, color: UrssafServiceStatEnumColor.STATUS_70_PAID.color, icon: UrssafServiceStatEnumColor.STATUS_70_PAID.icon }
    case UrssafServiceStatEnum.STATUS_110_CANCELED:
      return { text: UrssafServiceStatEnumColor.STATUS_110_CANCELED.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED, color: UrssafServiceStatEnumColor.STATUS_110_CANCELED.color, icon: UrssafServiceStatEnumColor.STATUS_110_CANCELED.icon }
    case UrssafServiceStatEnumColor.STATUS_110_CANCELED.value:
      return { text: UrssafServiceStatEnumColor.STATUS_110_CANCELED.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED, color: UrssafServiceStatEnumColor.STATUS_110_CANCELED.color, icon: UrssafServiceStatEnumColor.STATUS_110_CANCELED.icon }
    case UrssafServiceStatEnum.STATUS_111_CANCELED_111:
      return { text: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_111, color: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.color, icon: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.icon }
    case UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.value:
      return { text: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_111, color: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.color, icon: UrssafServiceStatEnumColor.STATUS_111_CANCELED_111.icon }
    case UrssafServiceStatEnum.STATUS_112_CANCELED_112:
      return { text: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_112, color: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.color, icon: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.icon }
    case UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.value:
      return { text: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_112, color: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.color, icon: UrssafServiceStatEnumColor.STATUS_112_CANCELED_112.icon }
    case UrssafServiceStatEnum.STATUS_120_COVERED:
      return { text: UrssafServiceStatEnumColor.STATUS_120_COVERED.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_112, color: UrssafServiceStatEnumColor.STATUS_120_COVERED.color, icon: UrssafServiceStatEnumColor.STATUS_120_COVERED.icon }
    case UrssafServiceStatEnumColor.STATUS_120_COVERED.value:
      return { text: UrssafServiceStatEnumColor.STATUS_120_COVERED.title, title: description ?? UrssafServiceStatusDescription.STATUS_CANCELED_112, color: UrssafServiceStatEnumColor.STATUS_120_COVERED.color, icon: UrssafServiceStatEnumColor.STATUS_120_COVERED.icon }
    default:
      return { text: UrssafServiceStatEnumColor.ERROR.title, title: `${defaulDescription}, veuillez contacter le service client de ${getActiveAppBrandConfig()}`, color: UrssafServiceStatEnumColor.ERROR.color, icon: UrssafServiceStatEnumColor.ERROR.icon }
  }
};

export const displayStatutCourse = (status: string, description: string, defaulDescription: string, isOver?: boolean, isStatus?: boolean, classes?: any, declarationStatus?: string | null, declarationStatusReason?: string | null) => {
  const model = resolveStatutCourse(status, description, defaulDescription, isOver, declarationStatus, declarationStatusReason);
  return statusModel(model.text, model.title, model.color, model.icon, isStatus, classes);
};

/**
 * Fiche de cours : le statut avec sa description visible sous la pastille,
 * sans avoir à survoler. La liste garde la pastille seule avec l'info-bulle.
 */
export const displayStatutCourseDetail = (status: string, description: string, defaulDescription: string, isOver?: boolean, classes?: any, declarationStatus?: string | null, declarationStatusReason?: string | null, align: "left" | "right" = "right") => {
  const model = resolveStatutCourse(status, description, defaulDescription, isOver, declarationStatus, declarationStatusReason);
  // align « left » : en-tête de la fenêtre repensée (pastille puis explication sous le titre)
  const left = align === "left";
  return (
    <div className={`flex flex-col gap-1 ${left ? "items-start" : "items-end max-w-xs sm:max-w-sm"}`}>
      {statusModel(model.text, model.title, model.color, model.icon, false, classes)}
      {model.title && (
        <p className={`text-xs text-gray-600 leading-snug ${left ? "text-left" : "text-right"} ${classes?.fontFamily ?? ""}`}>
          {model.title}
        </p>
      )}
    </div>
  );
};
//__________________- Objects____________________________///
export const months = [
  ["Janvier", "j"],
  ["Février", "f"],
  ["Mars", "m"],
  ["Avril", "a"],
  ["Mai", "m"],
  ["Juin", "j"],
  ["Juillet", "j"],
  ["Août", "a"],
  ["Septembre", "s"],
  ["Octobre", "o"],
  ["Novembre", "n"],
  ["Décembre", "d"],
];
export const weekDays = [
  ["Dimanche", "D"],
  ["Lundi", "L"],
  ["Mardi", "M"],
  ["Mercredi", "M"],
  ["Jeudi", "J"],
  ["Vendredi", "V"],
  ["Samedi", "S"],
];
export const days = [
  {
    fr: "Lundi",
    en: "monday",
  },
  {
    fr: "Mardi",
    en: "tuesday",
  },
  {
    fr: "Mercredi",
    en: "wednesday",
  },
  {
    fr: "Jeudi",
    en: "thursday",
  },
  {
    fr: "Vendredi",
    en: "friday",
  },
  {
    fr: "Samedi",
    en: "saturday",
  },
  {
    fr: "Dimanche",
    en: "sunday",
  },
];
export const lettreVoieList = [
  { name: "A", code: "A" },
  { name: "B", code: "B" },
  { name: "Bis", code: "B" },
  { name: "Quinquies", code: "C" },
  { name: "C", code: "C" },
  { name: "D", code: "D" },
  { name: "E", code: "E" },
  { name: "F", code: "F" },
  { name: "G", code: "G" },
  { name: "H", code: "H" },
  { name: "I", code: "I" },
  { name: "J", code: "J" },
  { name: "K", code: "K" },
  { name: "L", code: "L" },
  { name: "M", code: "M" },
  { name: "N", code: "N" },
  { name: "O", code: "O" },
  { name: "P", code: "P" },
  { name: "Q", code: "Q" },
  { name: "Quater", code: "Q" },
  { name: "R", code: "R" },
  { name: "S", code: "S" },
  { name: "T", code: "T" },
  { name: "Ter", code: "T" },
  { name: "U", code: "U" },
  { name: "V", code: "V" },
  { name: "W", code: "W" },
  { name: "X", code: "X" },
  { name: "Y", code: "Y" },
  { name: "Z", code: "Z" }
];
export const typeVoie = [
  { name: "Allée", code: "ALL" },
  { name: "Avenue", code: "AV" },
  { name: "Batiment", code: "BAT" },
  { name: "Boulevard", code: "BD" },
  { name: "Cours", code: "C" },
  { name: "Canal", code: "CAN" },
  { name: "CASERNE", code: "CAS" },
  { name: "CAVEE", code: "CAV" },
  { name: "CEINTURE", code: "CEI" },
  { name: "CELLIER", code: "CEL" },
  { name: "CENTRE", code: "CEN" },
  { name: "COLLEGE", code: "CGE" },
  { name: "CHASSE", code: "CH" },
  { name: "CHATEAU", code: "CHA" },
  { name: "Chemin", code: "CHE" },
  { name: "CHANTIER", code: "CHN" },
  { name: "CHAPELLE", code: "CHP" },
  { name: "Chaussée", code: "CHS" },
  { name: "CHEMINEMENT", code: "CHT" },
  { name: "CHEMIN VICINAL", code: "CHV" },
  { name: "Cité", code: "CI" },
  { name: "CIMETIERE", code: "CIM" },
  { name: "CINEMA", code: "CIN" },
  { name: "Clos", code: "CL" },
  { name: "CLUB", code: "CLB" },
  { name: "CLINIQUE", code: "CLI" },
  { name: "COL", code: "CLL" },
  { name: "CLOITRE", code: "CLO" },
  { name: "COIN", code: "CN" },
  { name: "COUR", code: "CO" },
  { name: "COLLINE", code: "COE" },
  { name: "Cottage", code: "COG" },
  { name: "COLONIE", code: "COL" },
  { name: "COMMISSARIAT", code: "COM" },
  { name: "CORNICHE", code: "COR" },
  { name: "COTE", code: "COT" },
  { name: "COULOIR", code: "COU" },
  { name: "CARROI", code: "CRO" },
  { name: "CROIX", code: "CRX" },
  { name: "CENTRAL TELEPHONIQUE", code: "CTE" },
  { name: "CURE", code: "CUR" },
  { name: "DEGRE", code: "DEG" },
  { name: "DEPOT", code: "DEP" },
  { name: "DESCENTE", code: "DES" },
  { name: "DIGUE", code: "DIG" },
  { name: "DOMAINE", code: "DOM" },
  { name: "DIRECTION", code: "DON" },
  { name: "ECART", code: "ECA" },
  { name: "ECLUSE", code: "ECL" },
  { name: "ECOLE", code: "ECO" },
  { name: "ENCLAVE", code: "ECV" },
  { name: "EGLISE", code: "EGL" },
  { name: "EMPLACEMENT", code: "EMP" },
  { name: "ENCEINTE", code: "EN" },
  { name: "ENCLOS", code: "ENC" },
  { name: "ENTREPOT", code: "ENT" },
  { name: "ESCALIER", code: "ESC" },
  { name: "ESPLANADE", code: "ESP" },
  { name: "ETABLISSEMENT", code: "ETA" },
  { name: "ETANG", code: "ETG" },
  { name: "FACADE", code: "FAC" },
  { name: "FACULTE", code: "FCU" },
  { name: "FERME", code: "FER" },
  { name: "Faubourg", code: "FG" },
  { name: "FOLIE", code: "FOL" },
  { name: "FONTAINE", code: "FON" },
  { name: "FORET", code: "FOR" },
  { name: "FOSSE", code: "FOS" },
  { name: "FOYER", code: "FOY" },
  { name: "FRICHE", code: "FRI" },
  { name: "FORT", code: "FT" },
  { name: "GARE", code: "GA" },
  { name: "GALERIE", code: "GAL" },
  { name: "GRAND IMPASSE", code: "GDI" },
  { name: "GENDARMERIE", code: "GED" },
  { name: "GRAND ENSEMBLE", code: "GEN" },
  { name: "GARAGE", code: "GGE" },
  { name: "GLACIS", code: "GLA" },
  { name: "GROUPE", code: "GPE" },
  { name: "GRAND RUE", code: "GR" },
  { name: "GRANDE RUE", code: "GR" },
  { name: "GRILLE", code: "GRI" },
  { name: "GARENNE", code: "GRN" },
  { name: "GROUPEMENT", code: "GRP" },
  { name: "GROUPE SCOLAIRE", code: "GSC" },
  { name: "GUE", code: "GUE" },
  { name: "HALAGE", code: "HA" },
  { name: "HALLE", code: "HAL" },
  { name: "Hameau", code: "HAM" },
  { name: "HOSPICE", code: "HCE" },
  { name: "HIPPODROME", code: "HIP" },
  { name: "H.L.M.", code: "HLM" },
  { name: "HOPITAL", code: "HOP" },
  { name: "HOTEL", code: "HOT" },
  { name: "HUTTE", code: "HUT" },
  { name: "ILE", code: "ILE" },
  { name: "ILOT", code: "ILO" },
  { name: "Immeuble", code: "IMB" },
  { name: "Impasse", code: "IMP" },
  { name: "INSTITUT", code: "INS" },
  { name: "INSPECTION", code: "ISP" },
  { name: "JARDIN", code: "JAR" },
  { name: "JETEE", code: "JET" },
  { name: "JUSTICE", code: "JUS" },
  { name: "LIEU DIT", code: "LD" },
  { name: "LEVEE", code: "LEV" },
  { name: "Lotissement", code: "LOT" },
  { name: "LYCEE", code: "LYC" },
  { name: "MARE", code: "MA" },
  { name: "MAGASIN", code: "MAG" },
  { name: "Maison", code: "MAI" },
  { name: "MANOIR", code: "MAN" },
  { name: "MARCHE", code: "MAR" },
  { name: "MAS", code: "MAS" },
  { name: "MATERNITE", code: "MAT" },
  { name: "METRO", code: "MET" },
  { name: "MAISON FORESTIERE", code: "MF" },
  { name: "MAISON DE GARDE", code: "MG" },
  { name: "MAIL", code: "ML" },
  { name: "MOULIN", code: "MOU" },
  { name: "MAIRIE", code: "MRI" },
  { name: "Montée", code: "MTE" },
  { name: "MUSEE", code: "MUS" },
  { name: "NOUVELLE ROUTE", code: "NTE" },
  { name: "OBSERVATOIRE", code: "OBS" },
  { name: "OCTROI", code: "OCT" },
  { name: "PARVIS", code: "PA" },
  { name: "PETITE AVENUE", code: "PAE" },
  { name: "PALAIS", code: "PAL" },
  { name: "Parc", code: "PAR" },
  { name: "Passage", code: "PAS" },
  { name: "PATURE", code: "PAT" },
  { name: "Pavillon", code: "PAV" },
  { name: "PRESBYTERE", code: "PBY" },
  { name: "PORCHE", code: "PCE" },
  { name: "PERYSTILE", code: "PER" },
  { name: "PHARMACIE", code: "PHA" },
  { name: "PIECE", code: "PIE" },
  { name: "PETITE IMPASSE", code: "PIM" },
  { name: "PARKING", code: "PKG" },
  { name: "Place", code: "PL" },
  { name: "PLAINE", code: "PLA" },
  { name: "PLACIS", code: "PLC" },
  { name: "PASSERELLE", code: "PLE" },
  { name: "PLATEAU", code: "PLT" },
  { name: "PETIT LYCEE", code: "PLY" },
  { name: "PONT", code: "PO" },
  { name: "POINTE", code: "POI" },
  { name: "Porte", code: "POR" },
  { name: "POTERNE", code: "POT" },
  { name: "POURTOUR", code: "POU" },
  { name: "PETIT PASSAGE", code: "PPA" },
  { name: "PETITE RUE", code: "PR" },
  { name: "PRAIRIE", code: "PRA" },
  { name: "PRE", code: "PRE" },
  { name: "PROMENADE", code: "PRO" },
  { name: "PRESQUILE", code: "PRQ" },
  { name: "PETITE ROUTE", code: "PRT" },
  { name: "PREAU", code: "PRU" },
  { name: "PORT", code: "PT" },
  { name: "POSTE", code: "PTE" },
  { name: "Quai", code: "Q" },
  { name: "Quartier", code: "QU" },
  { name: "Rue", code: "R" },
  { name: "RACCOURCI", code: "RAC" },
  { name: "Rampe", code: "RAM" },
  { name: "REMPART", code: "REM" },
  { name: "Résidence", code: "RES" },
  { name: "RESTAURANT", code: "RNT" },
  { name: "ROCADE", code: "ROC" },
  { name: "ROQUET", code: "ROQ" },
  { name: "ROTONDE", code: "ROT" },
  { name: "ROULADE", code: "ROU" },
  { name: "ROND POINT", code: "RP" },
  { name: "Route", code: "RTE" },
  { name: "Ruelle", code: "RUL" },
  { name: "SANATORIUM", code: "SAN" },
  { name: "SAPUM", code: "SAP" },
  { name: "SAS", code: "SAS" },
  { name: "SERVICE", code: "SCE" },
  { name: "SECTION", code: "SEC" },
  { name: "SEMINAIRE", code: "SEM" },
  { name: "Sentier", code: "SEN" },
  { name: "SENTE", code: "SNT" },
  { name: "SOCIETE", code: "SOC" },
  { name: "Square", code: "SQ" },
  { name: "STADE", code: "STA" },
  { name: "STAND", code: "STN" },
  { name: "STATION", code: "STO" },
  { name: "TERRASSE", code: "TE" },
  { name: "TERRAIN", code: "TEA" },
  { name: "TENUE", code: "TEN" },
  { name: "TERTRE", code: "TER" },
  { name: "THEATRE", code: "THE" },
  { name: "TOUR", code: "TOU" },
  { name: "TERRE PLEIN", code: "TP" },
  { name: "TRAVERSE", code: "TRA" },
  { name: "TRIEGE", code: "TRG" },
  { name: "TRIAGE", code: "TRI" },
  { name: "TUILERIE", code: "TUI" },
  { name: "USINE", code: "USI" },
  { name: "VAL", code: "VAL" },
  { name: "VIEUX CHEMIN", code: "VCH" },
  { name: "VELODROME", code: "VEL" },
  { name: "VENELLE", code: "VEN" },
  { name: "VILLAGE", code: "VGE" },
  { name: "VIADUC", code: "VIA" },
  { name: "VILLE", code: "VIL" },
  { name: "VILLA", code: "VLA" },
  { name: "VALLEE", code: "VLE" },
  { name: "VILLE NOUVELLE", code: "VNO" },
  { name: "Voie", code: "VO" },
  { name: "VALLON", code: "VON" },
  { name: "VOYEUL", code: "VOY" },
  { name: "ZAC", code: "ZAC" },
  { name: "ZONE INDUSTRIELLE", code: "ZI" },
  { name: "Z.U.P.", code: "ZUP" },
];